import { animate, state, style, transition, trigger } from '@angular/animations';
import { DecimalPipe, formatDate, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator } from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource } from '@angular/material/table';
import { MatTab, MatTabContent, MatTabGroup } from '@angular/material/tabs';
import { MatTooltip } from '@angular/material/tooltip';
import { FormatNumberPipe } from '@app/shared/pipes/formatnumber.pipe';
import { SchadenUebersicht } from '@gsm/entities';
import { MomentModule } from 'ngx-moment';
import { WINDOW } from 'ngx-window-token';
import { trimLeadingZeroes } from '../../../utils';
import { FilterValues, OverviewFilterComponent } from './overview-filter/overview-filter.component';

type SchadenUebersichtKey = keyof SchadenUebersicht;

const ITEMS_PER_PAGE_LABEL = 'Einträge pro Seite';
const OF_LABEL = 'von';
const FIRST_PAGE_LABEL = 'erste Seite';
const PREVIOUS_PAGE_LABEL = 'vorherige Seite';
const NEXT_PAGE_LABEL = 'nächste Seite';
const LAST_PAGE_LABEL = 'letzte Seite';

@Component({
  selector:      'gsm-overview',
  templateUrl:   './overview.component.html',
  styleUrls:     ['./overview.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  animations:    [
    trigger('detailExpand', [
      state('collapsed, void', style({
        height:    '0px',
        minHeight: '0',
        display:   'none'
      })),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('250ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      transition('expanded <=> void', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ],
  standalone:    true,
  imports:       [
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatIcon,
    OverviewFilterComponent,
    MatCard,
    NgIf,
    MatIconButton,
    MatButton,
    MatCheckbox,
    MatTooltip,
    MatTabGroup,
    MatTab,
    MatTabContent,
    MatProgressSpinner,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    NgClass,
    NgFor,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    DecimalPipe,
    MomentModule,
    FormatNumberPipe
  ]
})
export class OverviewComponent implements OnInit,
                                          OnChanges {
  private window = inject<Window>(WINDOW);

  displayedColumnsSchaden: string[] = [
    'iconToggle',
    'schadenNummer',
    'schadenDatum',
    'meldeDatum',
    'vertragsSparte',
    'kundeNachname',
    'schadenStatus',
    'checkListStatus',
    'aktionen',
    'gsmNotificationEnabled'
  ];

  displayedColumnsEntwurf: string[] = [
    'iconToggle',
    'polizzeNummer',
    'schadenDatum',
    'vertragsSparte',
    'kundeNachname',
    'schadenVariante',
    'aktionen'
  ];

  expandedElement: SchadenUebersicht | null;
  isARGE = false;

  reset: boolean;
  isSchadenTab = true;
  schadenUebersichtDataSource = new MatTableDataSource<SchadenUebersicht>();
  schadenEntwurfDataSource = new MatTableDataSource<SchadenUebersicht>();
  schadenPosition: number;
  _highlightedSchadenNummer: string;

  @Input() schadenUebersicht: SchadenUebersicht[];
  @Input() schadenEntwurf: SchadenUebersicht[];
  @Input() schadenOverviewIsLoading: boolean;
  @Input() entwurfOverviewIsLoading: boolean;
  @Input() isKbma: boolean;
  @Input() isDeleteDisabled: boolean;
  @Input() schwebebestand: SchadenUebersicht;
  @Input() highlightedSchadenNummer: string;

  @Output() selectSchaden = new EventEmitter<SchadenUebersicht>();
  @Output() deleteSchaden = new EventEmitter<SchadenUebersicht>();
  @Output() printSchaden = new EventEmitter<SchadenUebersicht>();
  @Output() showEsignDialog = new EventEmitter<SchadenUebersicht>();
  @Output() toggleGsmNotification = new EventEmitter<SchadenUebersicht>();
  @Output() toggleArge = new EventEmitter<boolean>();
  @Output() schadenMelden = new EventEmitter();
  @Output() deleteAllEntwuerfe = new EventEmitter();

  @ViewChild('paginatorSchaden') set paginatorSchaden(mp: MatPaginator) {
    if (mp) {
      mp._intl.itemsPerPageLabel = ITEMS_PER_PAGE_LABEL;
      mp._intl.getRangeLabel = this._setPaginatorRangeLabel();
      mp._intl.firstPageLabel = FIRST_PAGE_LABEL;
      mp._intl.previousPageLabel = PREVIOUS_PAGE_LABEL;
      mp._intl.nextPageLabel = NEXT_PAGE_LABEL;
      mp._intl.lastPageLabel = LAST_PAGE_LABEL;
      this.schadenUebersichtDataSource.paginator = mp;
    }
  }

  @ViewChild(MatSort)
  set sortUebersicht(v: MatSort) {
    this.schadenUebersichtDataSource.sort = v;
    this.schadenEntwurfDataSource.sort = v;
  }

  ngOnInit() {
    this.schadenUebersichtDataSource.filterPredicate = this.createFilter();
    this.schadenEntwurfDataSource.filterPredicate = this.createFilterEntwurf();
  }

  getHightlightedSchadenPos(): number {
    if (this.schadenUebersichtDataSource.filteredData && this.schadenUebersichtDataSource.sort) {
      const sortedUebersicht = this.schadenUebersichtDataSource.sortData(
        this.schadenUebersichtDataSource.filteredData,
        this.schadenUebersichtDataSource.sort
      );
      return sortedUebersicht.findIndex(schaden =>
        schaden.schadenNummer ? trimLeadingZeroes(schaden.schadenNummer) === this.highlightedSchadenNummer : false
      );
    }
  }

  triggerPaginaton(schadenPosition: number) {
    if (this.schadenUebersichtDataSource.paginator) {
      this.schadenUebersichtDataSource.paginator.pageIndex = Math.floor(schadenPosition / 10);
    }
  }

  ngOnChanges({
                schadenUebersicht,
                schadenEntwurf
              }: SimpleChanges) {
    if (schadenUebersicht) {
      this.schadenUebersichtDataSource.data = schadenUebersicht.currentValue || [];
      // navigate to page with highlighted schaden if highlightedSchadenNummer exists
      if (this.schadenUebersichtDataSource && this.highlightedSchadenNummer) {
        this.schadenPosition = this.getHightlightedSchadenPos();
        if (this.schadenPosition) {
          this.triggerPaginaton(this.schadenPosition);
        }
      }
    }
    if (schadenEntwurf) {
      this.schadenEntwurfDataSource.data = schadenEntwurf.currentValue || [];
    }
  }

  get metaDataString() {
    if (!this.schwebebestand) {
      return '';
    }

    const metaData = [''];

    if (this.schwebebestand.kundeVorname && this.schwebebestand.kundeNachname) {
      metaData.push(this.schwebebestand.kundeVorname + ' ' + this.schwebebestand.kundeNachname);
    }

    if (this.schwebebestand.schadenDatum) {
      const format = 'dd.MM.yyyy';
      const myDate = this.schwebebestand.schadenDatum;
      const locale = 'de-AT';
      const formattedDate = formatDate(myDate, format, locale);

      metaData.push(formattedDate);
    }
    if (this.schwebebestand.vertragsSparte) {
      metaData.push(this.schwebebestand.vertragsSparte);
    }

    return metaData.join(' / ');
  }

  selectItem(event, element: SchadenUebersicht) {
    event.stopPropagation();
    this.selectSchaden.emit(element);
  }

  deleteItem(event, element: SchadenUebersicht) {
    event.stopPropagation();
    this.deleteSchaden.emit(element);
  }

  printItem(event, element: SchadenUebersicht) {
    event.stopPropagation();
    this.printSchaden.emit(element);
  }

  deleteAllDrafts(event) {
    event.stopPropagation();
    this.deleteAllEntwuerfe.emit();
  }

  openEsignDialog(event, element: SchadenUebersicht) {
    event.stopPropagation();
    this.showEsignDialog.emit(element);
  }

  handleGsmNotificationToggle(event, element: SchadenUebersicht) {
    event.stopPropagation();
    this.toggleGsmNotification.emit(element);
  }

  onArgeCheckboxChange({checked}: MatCheckboxChange) {
    this.isARGE = checked;
    this.toggleArge.emit(checked);
  }

  toggleRowExpand(row) {
    this.expandedElement !== row ? (this.expandedElement = row) : (this.expandedElement = null);
  }

  applyGlobalFilter(filterText: string) {
    const gfValue = new FilterValues();
    const gfValueEntwurf = new FilterValues();
    gfValue.kundeNachname = filterText;
    gfValueEntwurf.kundeNachname = filterText;
    this.schadenUebersichtDataSource.filter = JSON.stringify(gfValue);
    this.schadenEntwurfDataSource.filter = JSON.stringify(gfValueEntwurf);
  }

  applyFilter(filterValues: FilterValues) {
    this.schadenUebersichtDataSource.filter = JSON.stringify(filterValues);
    this.schadenEntwurfDataSource.filter = JSON.stringify(filterValues);
  }

  onTabChange() {
    this.reset = true;
    this.isSchadenTab = !this.isSchadenTab;
    this.window.setTimeout(() => (this.reset = false), 500);
  }

  createFilter(): (data: SchadenUebersicht, filterString: string) => boolean {
    return (data: SchadenUebersicht, filterString: string): boolean => {
      const searchTerms = JSON.parse(filterString);

      const schadenUebersichtKeys: SchadenUebersichtKey[] = [
        'schadenNummer',
        'kundeNachname',
        'vertragsSparte',
        'schadenStatus',
        'checkListStatus',
        'schadenDatumEnd' as SchadenUebersichtKey,
        'meldeDatumEnd' as SchadenUebersichtKey,
        'letzterBearbeiterNachname',
        'betreuerNummer',
        'meldeDatum'
      ];
      const filterFunc = createKeyMatcher(data, searchTerms);
      return schadenUebersichtKeys.every(filterFunc);
    };
  }

  createFilterEntwurf() {
    return (data: SchadenUebersicht, filter: string): boolean => {
      const searchTerms = JSON.parse(filter);
      const schadenUebersichtKeys: SchadenUebersichtKey[] = [
        'polizzeNummer',
        'kundeNachname',
        'vertragsSparte',
        'schadenStatus',
        'schadenDatumEnd' as SchadenUebersichtKey,
        'letzterBearbeiterNachname',
        'betreuerNummer',
        'checkListStatus'
      ];
      const filterFunc = createKeyMatcher(data, searchTerms);

      return schadenUebersichtKeys.every(filterFunc);
    };
  }

  convertToCheckListStatusText(checkListStatus) {
    switch (checkListStatus) {
      case 'K':
        return 'Kein GSM Schaden';
      case 'P':
        return 'Prüfbar/Vollständig';
      case 'N':
        return 'Nicht prüfbar/Unvollständig';
      case 'T':
        return 'Teilweise prüfbar/Unvollständig';
      default:
        return 'Unbekannt';
    }
  }

  getCheckListItemStatus(checkListItemStatus) {
    switch (checkListItemStatus) {
      case 'O':
        return 'OFFEN';
      case 'N':
        return 'NICHT VORHANDEN';
      case 'H':
        return 'HINFÄLLIG';
      default:
        return '';
    }
  }

  getCheckboxLabel() {
    if (!this.isKbma && this.isSchadenTab) {
      return 'alle Schäden der ARGE';
    } else if (!this.isKbma && !this.isSchadenTab) {
      return 'alle Entwürfe der ARGE';
    } else if (this.isKbma && this.isSchadenTab) {
      return 'alle Schäden der Vertriebseinheit';
    } else if (this.isKbma && !this.isSchadenTab) {
      return 'alle Entwürfe der Vertreibseinheit';
    }
  }

  private _setPaginatorRangeLabel() {
    return (page: number, pageSize: number, length: number) => {
      if (length === 0 || pageSize === 0) {
        return `0 ${OF_LABEL} ${length}`;
      }
      length = Math.max(length, 0);
      const startIndex = page * pageSize;
      const endIndex = startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;
      return `${startIndex + 1} - ${endIndex} ${OF_LABEL} ${length}`;
    };
  }

  getTrimmedSchadennummer(schadennummer: string) {
    return trimLeadingZeroes(schadennummer);
  }
}

function createKeyMatcher(data, searchTerms): (prop: string) => boolean {
  return prop => {
    const fieldVal = data[prop];
    const termVal = searchTerms[prop];
    if (searchTerms.schadenDatumEnd.length && termVal === searchTerms.schadenDatumEnd) {
      return data.schadenDatum >= searchTerms.schadenDatumStart && data.schadenDatum <= searchTerms.schadenDatumEnd;
    } else if (searchTerms.meldeDatumEnd.length && termVal === searchTerms.meldeDatumEnd) {
      return data.meldeDatum >= searchTerms.meldeDatumStart && data.meldeDatum <= searchTerms.meldeDatumEnd;
    } else {
      if (!termVal) {
        // no search term => match!
        return true;
      }
      return fieldVal && fieldVal.toLowerCase()
        .indexOf(termVal.toLowerCase()) !== -1;
    }
  };
}
